export class FaceRecognitionError extends Error {
  constructor(
    public readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'FaceRecognitionError';
  }
}

export class NoFaceDetectedError extends FaceRecognitionError {
  constructor() { super('NO_FACE', 'No face detected in frame'); }
}

export class MultipleFacesError extends FaceRecognitionError {
  constructor(public readonly count: number) {
    super('MULTIPLE_FACES', `${count} faces detected — ensure only one face is visible`);
  }
}

export class LowQualityError extends FaceRecognitionError {
  constructor(
    public readonly subreason: 'blur' | 'brightness' | 'size',
    // QualityReport from vendor/types — contains blurVar, brightnessMean, faceShortSidePx, etc.
    public readonly report: {
      blurVar: number;
      brightnessMean: number;
      faceShortSidePx: number;
      blurOk: boolean;
      brightnessOk: boolean;
      sizeOk: boolean;
    },
  ) {
    super('LOW_QUALITY', `Frame quality too low: ${subreason}`);
  }

  /** Back-compat getter for scan flow (useScanLifecycle.ts). Returns subreason as string. */
  get reason(): string {
    return this.subreason;
  }
}

export class SpoofDetectedError extends FaceRecognitionError {
  constructor(public readonly score: number) {
    super('SPOOF', `Liveness check failed (score ${score.toFixed(2)})`);
  }
}

export class LivenessSpoofError extends FaceRecognitionError {
  readonly code = 'LIVENESS_SPOOF' as const;
  constructor(public readonly consecutiveCount: number) {
    super('LIVENESS_SPOOF', `Liveness spoof detected (${consecutiveCount} consecutive frames)`);
  }
}

export class LivenessInconclusiveError extends FaceRecognitionError {
  readonly code = 'LIVENESS_INCONCLUSIVE' as const;
  constructor(public readonly totalFrames: number) {
    super('LIVENESS_INCONCLUSIVE', `Liveness inconclusive after ${totalFrames} frames`);
  }
}

/**
 * @deprecated Use LivenessSpoofError or LivenessInconclusiveError instead.
 * Preserved for scan-flow back-compat (useScanLifecycle.ts).
 * Single-frame liveness classifier (passive 3-band) — replaced by aggregator in enroll flow.
 */
export class LivenessBorderlineError extends FaceRecognitionError {
  constructor(public readonly score: number) {
    super('LIVENESS_BORDERLINE', `Liveness borderline (score ${score.toFixed(2)})`);
  }
}

export class ModelLoadError extends FaceRecognitionError {
  constructor(message: string) { super('MODEL_LOAD', message); }
}

export class CameraDeniedError extends FaceRecognitionError {
  constructor(message: string) { super('CAMERA_DENIED', message); }
}

export class EmbeddingTimeoutError extends FaceRecognitionError {
  constructor() { super('EMBEDDING_TIMEOUT', 'inferEmbedding did not complete within the allowed time'); }
}
